import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import employee components (lazy-loaded for performance)
const EmployeesList = React.lazy(() => import("./EmployeesList"));
const NewEmployee = React.lazy(() => import("./NewEmployee"));
const EmployeeDetails = React.lazy(() => import("./EmployeeDetails"));
const BulkUploadEmployees = React.lazy(() => import("./BulkUploadEmployees"));
const EmployeeRoles = React.lazy(() => import("./EmployeeRoles"));
const AwardClassifications = React.lazy(() => import("./AwardClassifications"));
const Departments = React.lazy(() => import("./Departments"));
const EmployeeInvites = React.lazy(() => import("./EmployeeInvites"));
const MyDetails = React.lazy(() => import("./MyDetails"));

const employeesModuleConfig: ModuleConfig = {
    // Module Identity
    id: "employees",
    name: "Employee Management",
    description: "Manage employee records, HR information, and workforce data",
    version: "1.0.0",
    enabled: true,
    category: "hr",
    order: 3,
    icon: "👷",
    color: "#5A67D8",

    // Routes
    // Permissions are enforced server-side via employeesPermissions in the backend.
    // permissionLevel indicates which backend permission level is required.
    routes: [
        {
            path: "/employees/my-details",
            component: MyDetails,
            permissions: [],
            permissionLevel: "self",
        },
        {
            path: "/employees",
            component: EmployeesList,
            permissions: [],
            permissionLevel: "view",
        },
        {
            path: "/employees/new",
            component: NewEmployee,
            permissions: [],
            permissionLevel: "create",
        },
        {
            path: "/employees/invites",
            component: EmployeeInvites,
            permissions: [],
            permissionLevel: "view",
        },
        {
            path: "/employees/:id",
            component: EmployeeDetails,
            permissions: [],
            permissionLevel: "view",
        },
        {
            path: "/employees/edit/:id",
            component: NewEmployee, // Reuse NewEmployee component for editing
            permissions: [],
            permissionLevel: "edit",
        },
        {
            path: "/employees/bulk-upload",
            component: BulkUploadEmployees,
            permissions: [],
            permissionLevel: "create",
        },
        {
            path: "/employees/roles",
            component: EmployeeRoles,
            permissions: [],
            permissionLevel: "edit",
        },
        {
            path: "/employees/award-classifications",
            component: AwardClassifications,
            permissions: [],
            permissionLevel: "edit",
        },
        {
            path: "/employees/departments",
            component: Departments,
            permissions: [],
            permissionLevel: "edit",
        }
    ],

    // Navigation
    navigation: [
        {
            label: "My Details",
            path: "/employees/my-details",
            icon: "👤",
            permissions: [],
            permissionLevel: "self",
        },
        {
            label: "All Employees",
            path: "/employees",
            icon: "👥",
            permissions: [],
            permissionLevel: "view",
        },
        {
            label: "Add Employee",
            path: "/employees/new",
            icon: "➕",
            permissions: [],
            permissionLevel: "create",
        },
        {
            label: "Bulk Upload",
            path: "/employees/bulk-upload",
            icon: "📤",
            permissions: [],
            permissionLevel: "create",
        },
        {
            label: "Invites",
            path: "/employees/invites",
            icon: "📧",
            permissions: [],
            permissionLevel: "view",
        },
        {
            label: "Manage Roles",
            path: "/employees/roles",
            icon: "🏷️",
            permissions: [],
            permissionLevel: "edit",
        },
        {
            label: "Award Classifications",
            path: "/employees/award-classifications",
            icon: "📋",
            permissions: [],
            permissionLevel: "edit",
        },
        {
            label: "Departments",
            path: "/employees/departments",
            icon: "🏢",
            permissions: [],
            permissionLevel: "edit",
        }
    ],

    // Permissions - enforced server-side via employeesPermissions in the backend resolvers
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
    },

    // Quick Actions
    quickActions: [
        {
            label: "Add Employee",
            path: "/employees/new",
            icon: "➕",
            description: "Register a new employee in the system"
        },
        {
            label: "View All",
            path: "/employees",
            icon: "👥",
            description: "View all employees"
        },
        {
            label: "Bulk Upload",
            path: "/employees/bulk-upload",
            icon: "📤",
            description: "Import multiple employees from CSV"
        }
    ]
};

export default employeesModuleConfig;